import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'fullpagescv_credentials';

export const useCredentials = () => {
  const [credentials, setCredentials] = useState({});
  const credentialsRef = useRef(credentials);

  // Keep ref in sync
  useEffect(() => {
    credentialsRef.current = credentials;
  }, [credentials]);

  // Load credentials from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setCredentials(JSON.parse(stored));
      }
    } catch {
      setCredentials({});
    }
  }, []);

  // Save credentials to localStorage whenever they change
  const saveCredentials = useCallback((newCredentials) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newCredentials));
      setCredentials(newCredentials);
    } catch (e) {
      console.error('Failed to save credentials:', e);
    }
  }, []);

  // Update credentials for a specific platform (now includes links and notes)
  const updatePlatformCredentials = useCallback((platformUrl, username, password, links = [], notes = '') => {
    const newCredentials = {
      ...credentialsRef.current,
      [platformUrl]: { username, password, links, notes }
    };
    saveCredentials(newCredentials);
  }, [saveCredentials]);

  // Get credentials for a specific platform
  const getPlatformCredentials = useCallback((platformUrl) => {
    return credentials[platformUrl] || { username: '', password: '', links: [], notes: '' };
  }, [credentials]);

  // Check if a platform has saved data (credentials, links, or notes)
  const hasCredentials = useCallback((platformUrl) => {
    const creds = credentials[platformUrl];
    return creds && (creds.username || creds.password || (creds.links && creds.links.length > 0) || creds.notes);
  }, [credentials]);

  // Clear credentials for a specific platform
  const clearPlatformCredentials = useCallback((platformUrl) => {
    const newCredentials = { ...credentialsRef.current };
    delete newCredentials[platformUrl];
    saveCredentials(newCredentials);
  }, [saveCredentials]);

  // Clear all credentials
  const clearAllCredentials = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCredentials({});
  }, []);

  // Export all credentials as JSON string
  const exportCredentials = useCallback(() => {
    return JSON.stringify(credentialsRef.current);
  }, []);

  // Import credentials from JSON string (merges with existing)
  const importCredentials = useCallback((jsonString, replace = false) => {
    const imported = JSON.parse(jsonString);
    const merged = replace ? imported : { ...credentialsRef.current, ...imported };
    saveCredentials(merged);
    return Object.keys(imported).length;
  }, [saveCredentials]);

  return {
    credentials,
    updatePlatformCredentials,
    getPlatformCredentials,
    hasCredentials,
    clearPlatformCredentials,
    clearAllCredentials,
    exportCredentials,
    importCredentials
  };
};

export default useCredentials;
